import { useState, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "../App";
import { API } from "../config";
import { useDigitalGoods } from "./useDigitalGoods";
import { useLanguage } from "../contexts/LanguageContext";
import { PLAY_STORE_URL, isPlayStoreApp } from "../lib/products";
import { openMollieCheckout } from "../lib/mollieCheckout";

const authJson = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("session_token")}`,
});

// Achat unifié : Google Play Billing dans l'app, Mollie sur le web
export function usePurchase() {
  const auth = useAuth();
  const { language } = useLanguage();
  const { isAvailable, makePurchase, consume } = useDigitalGoods();
  const [busy, setBusy] = useState(null);
  const fr = language === "fr";

  const buyGooglePlay = useCallback(async (sku, isSubscription) => {
    const res = await makePurchase(sku);
    const r = await fetch(`${API}/billing/verify-purchase`, {
      method: "POST", headers: authJson(),
      body: JSON.stringify({ sku: res.sku, purchaseToken: res.purchaseToken, isSubscription }),
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data.detail || "Erreur");
    // Pack consommable : on le consomme pour pouvoir le racheter (le serveur peut l'avoir déjà fait)
    if (!isSubscription) consume(res.purchaseToken).catch(() => {});
    auth?.setUser?.((prev) => ({
      ...prev,
      ...(data.new_balance !== undefined ? { credits: data.new_balance } : {}),
      ...(data.subscription ? { subscription: data.subscription, isPremium: true } : {}),
    }));
    toast.success(fr ? "Achat réussi ! Merci 💖" : "Purchase successful! 💖");
    return true;
  }, [makePurchase, consume, auth, fr]);

  const buyWeb = useCallback(async (sku, inline, isSubscription) => {
    if (isPlayStoreApp()) {
      toast.error(fr ? "Google Play n'est pas encore prêt, réessayez dans un instant." : "Google Play isn't ready yet, please try again in a moment.");
      return false;
    }
    const cfg = await (await fetch(`${API}/payments/config`)).json();
    if (cfg.mollie_enabled) return openMollieCheckout(sku, isSubscription);
    if (inline) return "web_unavailable";
    toast.info(fr ? "Paiement web bientôt disponible. Achetez dès maintenant depuis l'application Android."
      : "Web payment coming soon. Buy now from the Android app.", {
      action: { label: "Google Play", onClick: () => window.open(PLAY_STORE_URL, "_blank") },
    });
    return false;
  }, [fr]);

  const buy = useCallback(async (sku, isSubscription = false, { inline = false } = {}) => {
    setBusy(sku);
    try {
      return isAvailable ? await buyGooglePlay(sku, isSubscription) : await buyWeb(sku, inline, isSubscription);
    } catch (e) {
      if (!/cancel/i.test(e.message || "")) toast.error(e.message || (fr ? "Erreur lors de l'achat" : "Purchase error"));
      return false;
    } finally {
      setBusy(null);
    }
  }, [isAvailable, buyGooglePlay, buyWeb, fr]);

  return { buy, busy, isGooglePlay: isAvailable };
}
